import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BookRide.css';

function BookRide() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [selectedRide, setSelectedRide] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const popularLocations = [
    { id: 1, name: 'Saint Peter\'s University', icon: '🎓', address: 'Jersey City, NJ' },
    { id: 2, name: 'Penn Station', icon: '🚂', address: 'New York, NY' },
    { id: 3, name: 'Newark Airport', icon: '✈️', address: 'Newark, NJ' },
    { id: 4, name: 'Hoboken', icon: '🌃', address: 'Hoboken, NJ' },
    { id: 5, name: 'Journal Square', icon: '🚇', address: 'Jersey City, NJ' },
    { id: 6, name: 'Newport Mall', icon: '🛍️', address: 'Jersey City, NJ' }
  ];

  const rideOptions = [
    {
      id: 'economy',
      name: 'Economy',
      icon: '🚗',
      description: '4 seats • Affordable rides',
      price: 8,
      eta: '3 min',
      multiplier: 1
    },
    {
      id: 'comfort',
      name: 'Comfort',
      icon: '🚙',
      description: '4 seats • Extra legroom',
      price: 12,
      eta: '5 min',
      multiplier: 1.5
    },
    {
      id: 'pool',
      name: 'Pool',
      icon: '👥',
      description: 'Share & save up to 40%',
      price: 5,
      eta: '8 min',
      multiplier: 0.6
    },
    {
      id: 'xl',
      name: 'XL',
      icon: '🚐',
      description: '6 seats • For groups',
      price: 18,
      eta: '7 min',
      multiplier: 2.2
    }
  ];

  const handleLocationSelect = (location, type) => {
    if (type === 'pickup') {
      setPickup(location.name);
    } else {
      setDropoff(location.name);
    }
  };

  const handleSearch = () => {
    if (pickup && dropoff) {
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        setStep(2);
      }, 1500);
    }
  };

  const handleBookRide = () => {
    if (selectedRide) {
      setStep(3);
      // Simulate booking
      setTimeout(() => {
        navigate('/ride-confirmed', {
          state: {
            ride: selectedRide,
            pickup,
            dropoff
          }
        });
      }, 2000);
    }
  };

  return (
    <div className="book-ride-page">
      {/* Header */}
      <header className="book-ride-header">
        <button className="back-button" onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="header-title">
          {step === 1 && 'Book a Ride'}
          {step === 2 && 'Choose Ride'}
          {step === 3 && 'Confirming...'}
        </h1>
        <div className="header-spacer"></div>
      </header>

      {/* Step 1: Location Selection */}
      {step === 1 && (
        <div className="location-step">
          {/* Location Inputs */}
          <div className="location-inputs">
            <div className="location-input-group">
              <div className="location-dot green"></div>
              <div className="location-line"></div>
              <div className="location-dot red"></div>
            </div>
            <div className="location-fields">
              <div className="location-field">
                <input
                  type="text"
                  placeholder="Pickup location"
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  className="location-input"
                />
                {pickup && (
                  <button className="clear-btn" onClick={() => setPickup('')}>×</button>
                )}
              </div>
              <div className="field-divider"></div>
              <div className="location-field">
                <input
                  type="text"
                  placeholder="Where to?"
                  value={dropoff}
                  onChange={(e) => setDropoff(e.target.value)}
                  className="location-input"
                />
                {dropoff && (
                  <button className="clear-btn" onClick={() => setDropoff('')}>×</button>
                )}
              </div>
            </div>
          </div>

          {/* Popular Locations */}
          <div className="popular-locations">
            <h3 className="section-label">Popular Destinations</h3>
            <div className="locations-list">
              {popularLocations.map((location) => (
                <div
                  key={location.id}
                  className="location-item"
                  onClick={() => handleLocationSelect(location, pickup ? 'dropoff' : 'pickup')}
                >
                  <div className="location-item-icon">
                    <span>{location.icon}</span>
                  </div>
                  <div className="location-item-info">
                    <span className="location-item-name">{location.name}</span>
                    <span className="location-item-address">{location.address}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Search Button */}
          <div className="bottom-action">
            <button
              className={`search-rides-btn ${pickup && dropoff ? 'active' : ''}`}
              onClick={handleSearch}
              disabled={!pickup || !dropoff || isSearching}
            >
              {isSearching ? (
                <span className="loading-spinner"></span>
              ) : (
                'Search Rides'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Ride Selection */}
      {step === 2 && (
        <div className="ride-step">
          {/* Trip Summary */}
          <div className="trip-summary">
            <div className="trip-route">
              <div className="route-point">
                <div className="point-dot green"></div>
                <span>{pickup}</span>
              </div>
              <div className="route-line-h"></div>
              <div className="route-point">
                <div className="point-dot red"></div>
                <span>{dropoff}</span>
              </div>
            </div>
          </div>

          {/* Ride Options */}
          <div className="ride-options">
            <h3 className="section-label">Available Rides</h3>
            <div className="options-list">
              {rideOptions.map((ride) => (
                <div
                  key={ride.id}
                  className={`ride-option ${selectedRide?.id === ride.id ? 'selected' : ''}`}
                  onClick={() => setSelectedRide(ride)}
                >
                  <div className="ride-option-left">
                    <div className="ride-option-icon">
                      <span>{ride.icon}</span>
                    </div>
                    <div className="ride-option-info">
                      <span className="ride-option-name">{ride.name}</span>
                      <span className="ride-option-desc">{ride.description}</span>
                    </div>
                  </div>
                  <div className="ride-option-right">
                    <span className="ride-option-eta">{ride.eta}</span>
                    <span className="ride-option-price">${ride.price}</span>
                  </div>
                  {selectedRide?.id === ride.id && (
                    <div className="selected-check">✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Payment Method */}
          <div className="payment-method">
            <div className="payment-icon">💳</div>
            <div className="payment-info">
              <span className="payment-label">Payment</span>
              <span className="payment-value">ONTAP Wallet • $25.00</span>
            </div>
            <span className="payment-arrow">›</span>
          </div>

          {/* Book Button */}
          <div className="bottom-action">
            <button
              className={`book-ride-btn ${selectedRide ? 'active' : ''}`}
              onClick={handleBookRide}
              disabled={!selectedRide}
            >
              {selectedRide ? `Book ${selectedRide.name} • $${selectedRide.price}` : 'Select a ride'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirming */}
      {step === 3 && (
        <div className="confirming-step">
          <div className="confirming-animation">
            <div className="car-animation">🚗</div>
            <div className="pulse-ring"></div>
            <div className="pulse-ring delay"></div>
          </div>
          <h2 className="confirming-title">Finding your driver...</h2>
          <p className="confirming-subtitle">This usually takes less than a minute</p>
        </div>
      )}
    </div>
  );
}

export default BookRide;
